import type { CatalogEntry, PriceTier } from '../services/inferenceApi';

export const CATALOG_CATEGORIES = [
  { key: 'text', label: 'Text' },
  { key: 'image', label: 'Image' },
  { key: 'embeddings', label: 'Embeddings' },
  { key: 'audio', label: 'Audio' },
  { key: 'video', label: 'Video' },
  { key: 'rerank', label: 'Rerank' },
  { key: 'speech', label: 'Speech' },
  { key: 'transcription', label: 'Transcription' },
] as const;

// Pill tabs used on the Models catalog page. They map legacy API categories
// (text/image/video/embeddings) and common tags into the proposal's UI tabs.
export const CATALOG_TABS = [
  { key: 'all', label: 'All' },
  { key: 'chat', label: 'Chat' },
  { key: 'code', label: 'Code' },
  { key: 'embeddings', label: 'Embeddings' },
  { key: 'vision', label: 'Vision' },
] as const;

type TabKey = (typeof CATALOG_TABS)[number]['key'];

const TAB_MATCHERS: Record<TabKey, (m: CatalogEntry) => boolean> = {
  all: () => true,
  chat: (m) => ['text', 'chat'].includes(m.category) || m.tags.includes('chat'),
  code: (m) => m.category === 'code' || m.tags.includes('code'),
  embeddings: (m) => ['embeddings', 'embed'].includes(m.category) || m.tags.includes('embeddings'),
  vision: (m) => ['image', 'video', 'vision'].includes(m.category) || m.tags.includes('vision'),
};

const KNOWN = new Set<string>(CATALOG_CATEGORIES.map((c) => c.key));
const NEW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

export function isNew(createdAt: string, now: Date = new Date()): boolean {
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return false;
  const age = now.getTime() - t;
  return age >= 0 && age <= NEW_WINDOW_MS;
}

export function categoryCounts(models: CatalogEntry[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const m of models) {
    if (KNOWN.has(m.category)) counts[m.category] = (counts[m.category] ?? 0) + 1;
  }
  return counts;
}

export function filterByCategory(models: CatalogEntry[], category: string): CatalogEntry[] {
  if (category === 'all') return models;
  if (!KNOWN.has(category)) return [];
  return models.filter((m) => m.category === category);
}

export function tabCounts(models: CatalogEntry[]): Record<TabKey, number> {
  const counts = {} as Record<TabKey, number>;
  for (const t of CATALOG_TABS) {
    counts[t.key] = models.filter(TAB_MATCHERS[t.key]).length;
  }
  return counts;
}

export function filterByTab(models: CatalogEntry[], tab: string): CatalogEntry[] {
  const matcher = TAB_MATCHERS[tab as TabKey];
  if (!matcher) return [];
  return models.filter(matcher);
}

const SYMBOLS: Record<string, string> = { INR: '₹', USD: '$' };

// Unit labels for non-token billing units. per_1m_tokens (or an unrecognized/
// absent unit) falls back to the two-line token display.
const UNIT_LABELS: Record<string, string> = {
  per_request: 'request',
  per_image: 'image',
  per_video_second: 'video-sec',
  per_audio_minute: 'audio-min',
  per_100_frames: '100 frames',
};

// Format price with smart precision: up to 4 decimals for sub-cent values (0 < n < 0.01),
// otherwise 2 decimals with trailing zeros trimmed.
// Examples: 0 -> "0", 0.0084 -> "0.0084", 0.004 -> "0.004", 0.00004 -> "<0.0001", 0.08 -> "0.08", 10 -> "10", 12.5 -> "12.5".
function formatAmount(n: number): string {
  if (n === 0) return '0';
  // For sub-cent values (0 < n < 0.01), use 4 decimals to show precision
  if (n > 0 && n < 0.01) {
    const fourDecimal = Number(n.toFixed(4));
    if (fourDecimal === 0) return '<0.0001';
    return String(fourDecimal);
  }
  // For all other values >= 0.01, use 2 decimals
  return String(Number(n.toFixed(2)));
}

export function formatPrice(
  entry: CatalogEntry,
  envelopeCurrency: string,
): { input: string; output: string | null } | null {
  if (!entry.pricing_available) return null;
  const currency = entry.currency || envelopeCurrency;
  const sym = SYMBOLS[currency] ?? `${currency} `;
  const unitLabel = UNIT_LABELS[entry.billing_unit];
  if (entry.price_tiers.length > 0) {
    // Headline the default tier's price (== input_price_per_1m, guaranteed
    // by the catalog API for tiered rows). Only mark it "from " when the
    // slabs actually carry different prices - a single tier, or several
    // tiers all priced the same, isn't really a range.
    const defaultTier = entry.price_tiers.find((t) => t.default) ?? entry.price_tiers[0];
    const pricesDiffer = entry.price_tiers.some((t) => t.unit_price !== defaultTier.unit_price);
    const prefix = pricesDiffer ? 'from ' : '';
    return {
      input: `${prefix}${sym}${formatAmount(defaultTier.unit_price)} / ${unitLabel ?? '1M in'}`,
      output: null,
    };
  }
  if (unitLabel) {
    return {
      input: `${sym}${formatAmount(entry.input_price_per_1m)} / ${unitLabel}`,
      output: null,
    };
  }
  return {
    input: `${sym}${formatAmount(entry.input_price_per_1m)} / 1M in`,
    output: `${sym}${formatAmount(entry.output_price_per_1m)} / 1M out`,
  };
}

// Formats a single slab's price for the details modal's tier table - same
// symbol/precision/unit-label rules as formatPrice's headline, just for an
// arbitrary tier instead of the default one.
export function formatTierPrice(entry: CatalogEntry, tier: PriceTier, envelopeCurrency: string): string {
  const currency = entry.currency || envelopeCurrency;
  const sym = SYMBOLS[currency] ?? `${currency} `;
  const unitLabel = UNIT_LABELS[entry.billing_unit] ?? '1M in';
  return `${sym}${formatAmount(tier.unit_price)} / ${unitLabel}`;
}

// Formats a bare amount (no unit suffix) using the same currency-resolution
// and precision rules as formatPrice/formatTierPrice - used by the
// playground's live cost estimate line, which has no fixed "per X" unit
// since it already folds the estimated quantity into the amount.
export function formatCurrencyAmount(entry: CatalogEntry, amount: number, envelopeCurrency: string): string {
  const currency = entry.currency || envelopeCurrency;
  const sym = SYMBOLS[currency] ?? `${currency} `;
  return `${sym}${formatAmount(amount)}`;
}

export function toggleCompare(selection: string[], id: string): string[] {
  if (selection.includes(id)) return selection.filter((s) => s !== id);
  const next = [...selection, id];
  return next.length > 2 ? next.slice(next.length - 2) : next;
}
